<template>
    <v-app :dir="direction" :class="mainClasses">
        <v-content >
            <router-view v-if="isReady"
                         :class='{"dialog-open": isDialogOpen}'/>

            <IconsLoader :size="loaderSize" class="app-loader" v-if="showLoader"/>
        </v-content>
      <component is="style">{{brandingColors}}</component>
    </v-app>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {postMessageService} from "@/modules/common/services";
import {IconsLoader} from "@/modules/common/components";
import {urlUtil, stringUtil} from '@/modules/common/utils'
import moment from "moment"

export default {
  name: "App",
  components: {
    IconsLoader
  },
  data() {
    return {
      isReady: false
    }
  },
  async created () {
    // When vue app is ready and account service is ready (AngularJS) - listen to notification center initialize
    this.$frontageApi.listen('initNotificationCenter', this.initNotification);
    this.$frontageApi.listen('reload-account', this.reloadAccount);
    this.$setKeys('en', window.app_config.translations.en)
    const lang = urlUtil.getParameterByName("lang")
    if (lang) {
      await this.$loadLang(lang);
      this.$ds.setLocale(lang);
      this.$setLang(lang)
      moment.locale(lang)
      this.$vuetify.lang.current = lang
    }

    if(this.lang === 'he'){
      this.$vuetify.rtl = true
    }
    this.checkForcedFeatureFlag()
    this.$store.dispatch({type: "initWindowLocation"})
    postMessageService.post('app_loaded')
    this.isReady = true;

    // Set vuetify theme colors according to brand
    this.$vuetify.theme.themes.light.primary = stringUtil.rgbToHex(this.primaryColor);
    this.$vuetify.theme.themes.light.secondary = {
      base: stringUtil.rgbToHex(this.secondaryColor),
      lighten1: stringUtil.pSBC(0.6, this.secondaryColor, false, true),
      lighten2: stringUtil.pSBC(0.76, this.secondaryColor, false, true),
      lighten3: stringUtil.pSBC(0.93, this.secondaryColor, false, true)
    }
  },
  destroyed() {
    this.$frontageApi.remove('initNotificationCenter', this.initNotification)
  },
  computed: {
    ...mapGetters(['lang', 'isMobile', 'isMobileApp', 'dialogStatus', 'showLoader', 'isChromeIos', 'isiPhone', 'isiPad']),
    ...mapGetters("accountModule", ["pendo","isStar","account"]),
    ...mapGetters("brandModule", ["secondaryColor","primaryColor","brandColor","textColor"]),
    mainClasses() {
      return {
        rtl: this.lang === 'he',
        chromeIos: this.isChromeIos,
        iPhone: this.isiPhone,
        iPad: this.isiPad,
        mobile: this.isMobile,
        mobileApp: this.isMobileApp,
      }
    },
    direction() {
      return this.lang === 'he' ? 'rtl' : 'ltr'
    },
    isDialogOpen(){
      return this.dialogStatus.isOpen
    },
    loaderSize() {
      return this.isMobile ? "sm" : "md"
    },
    brandingColors() {
      //Convert brand colors to hex
      let secondary = stringUtil.pSBC(0, this.secondaryColor, '#000000')
      let primary = stringUtil.pSBC(0, this.primaryColor, '#000000')
      let brandColor = stringUtil.pSBC(0, this.brandColor, '#000000')
      let textColor = stringUtil.pSBC(0, this.textColor, '#000000')
      return `
        body {
        --brand-color-secondary: ${secondary};
        --brand-color-primary: ${primary};
        --brand-color: ${brandColor};
        --brand-text-color: ${textColor};
        //convert to (red, green, blue) string that can be used in rgba();
        --brand-color-secondary-rgb: ${stringUtil.hexToRgb(secondary)};
        --brand-color-primary-rgb: ${stringUtil.hexToRgb(primary)};
        --brand-color-rgb: ${stringUtil.hexToRgb(brandColor)};
        --brand-text-color-rgb: ${stringUtil.hexToRgb(textColor)};
        }`
    }
  },
  methods: {
    ...mapActions("accountModule", ["fetchAccount"]),
    ...mapActions("notificationCenterModule", ["initNotificationCenterBadge"]),
    checkForcedFeatureFlag() {
      if (!this.isMobileApp &&  process.env.VUE_APP_ENV !== "production") {
        let forceFeatureFlag = urlUtil.getParameterByName("forceFeatureFlagName",window.parent.location.href)
        if (forceFeatureFlag) {
          this.$store.commit("addForceFeature",forceFeatureFlag);
       }
      }
    },
    initNotification() {
      this.fetchAccount()
      this.initNotificationCenterBadge()
    },
    reloadAccount() {
      this.fetchAccount()
    }
  },
  watch: {
    primaryColor: function(newVal, oldVal) {
      // Set vuetify theme colors according to brand
      this.$vuetify.theme.themes.light.primary = stringUtil.rgbToHex(newVal);
    },
    secondaryColor: function(newVal, oldVal) {
      // Set vuetify theme colors according to brand
      this.$vuetify.theme.themes.light.secondary = {
        base: stringUtil.rgbToHex(newVal),
        lighten1: stringUtil.pSBC(0.6, newVal, false, true),
        lighten2: stringUtil.pSBC(0.76,newVal, false, true),
        lighten3: stringUtil.pSBC(0.93, newVal, false, true)
      }
   },
  }
}
</script>

<style lang="scss" >
    #app{
        background: transparent;
        min-height: 100vh;
    }

    .v-content{
      max-height: 100%;
    }

    .rtl {
        direction: rtl;
        text-align: right;
    }

    .app-loader {
      position: fixed;
      z-index: 1;
      top: 0;
    }
</style>
